// Phase 18 — theme preference (light / dark / system).
//
// The applied class (`.dark` on <html>) is set before first paint by the inline
// script in THEME_INIT_SCRIPT (see __root). This module manages the persisted
// *preference* and re-applies on change, sharing one store across the app via
// useSyncExternalStore. SSR-safe: the server snapshot is always "system".

import { useSyncExternalStore } from "react";

export type ThemePreference = "light" | "dark" | "system";
export const THEME_STORAGE_KEY = "coolsymbols.theme";

/**
 * Blocking inline script — runs in <head> before the body paints so there is no
 * light/dark flash. Mirrors resolveTheme() below; kept as a string so it can be
 * injected verbatim. scripts/validate.mjs checks this constant exists.
 */
export const THEME_INIT_SCRIPT = `(function(){try{var k="${THEME_STORAGE_KEY}";var p=localStorage.getItem(k);var m=window.matchMedia("(prefers-color-scheme: dark)").matches;var dark=p==="dark"||(p==="system"&&m);document.documentElement.classList.toggle("dark",dark);}catch(e){}})();`;

// Light is the default when the visitor has expressed no preference. Choosing
// "System" explicitly (or "Dark") still follows the OS / that choice.
let pref: ThemePreference = "light";
let hydrated = false;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

/** Resolve a preference to the concrete "light" | "dark" actually applied. */
export function resolveTheme(p: ThemePreference): "light" | "dark" {
  if (p === "system") return systemPrefersDark() ? "dark" : "light";
  return p;
}

function apply(p: ThemePreference) {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", resolveTheme(p) === "dark");
}

function hydrate() {
  if (hydrated || typeof window === "undefined") return;
  hydrated = true;
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") pref = raw;
  } catch {
    /* private mode — keep default */
  }
  // Keep "system" in sync with OS changes while mounted.
  if (typeof window.matchMedia === "function") {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      if (pref === "system") {
        apply(pref);
        emit();
      }
    };
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
  }
}

function subscribe(fn: () => void): () => void {
  hydrate();
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function getSnapshot(): ThemePreference {
  return pref;
}
function getServerSnapshot(): ThemePreference {
  return "light";
}

/** React hook — current theme preference, re-renders on change. */
export function useTheme(): ThemePreference {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function setTheme(p: ThemePreference): void {
  hydrate();
  pref = p;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, p);
  } catch {
    /* ignore quota / private mode */
  }
  apply(p);
  emit();
}
